import React from 'react';
import uuid from 'uuid-random';
import { styled } from '@mui/system';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import SelectChip from 'components/SelectChip';
import { getLanguage } from 'translation';

const skillLabel = (option) => {
  if (!option) return '';
  if (typeof option.name === 'string') return option.name;
  if (option.name && typeof option.name === 'object') {
    return option.name.ua || option.name.en || Object.values(option.name)[0] || '';
  }
  return option.value || option.title || '';
};

const localized = (obj, lang) => {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  return obj[lang] || obj.en || obj.ua || Object.values(obj)[0] || '';
};

const FiltersWrapper = styled('div')(() => ({
  paddingBottom: 20,
  paddingTop: 20,
  '& .MuiOutlinedInput-root': {
    minHeight: 44
  },
  '& .MuiSelect-select, & .MuiOutlinedInput-input': {
    fontSize: 14
  },
  '& .MuiSelect-select': {
    paddingTop: 0,
    paddingBottom: 0,
    display: 'flex',
    alignItems: 'center'
  },
  '& .MuiAutocomplete-root .MuiOutlinedInput-root': {
    padding: '4px 8px'
  },
  '& .MuiInputLabel-root': {
    fontSize: 14,
    transform: 'translate(14px, 12px) scale(1)'
  },
  '& .MuiInputLabel-shrink': {
    transform: 'translate(14px, -8px) scale(0.85)',
    backgroundColor: '#ffffff',
    padding: '0 4px'
  },
  '& .MuiChip-root': {
    height: 24,
    fontSize: 12
  }
}));

const FiltersTitle = styled(Typography)(() => ({
  fontWeight: 600,
  marginBottom: 10,
  padding: '9px 0'
}));

const Filters = ({
  categories,
  tags,
  skills,
  handleChangeCategories,
  handleChangeTags,
  handleChangeSkills,
  t,
  filters,
  actions
}) => {
  const [key, setKey] = React.useState(uuid());
  const lang = React.useMemo(() => (getLanguage() || 'en').toLowerCase(), []);

  // Group tags by name (the tag schema uses `name` as the group label and
  // `value` as the specific tag). One dropdown per group, all writing into
  // the same shared `tag_ids` filter. The backend pre-localizes tag.name
  // and tag.value into strings in the request language — hidden groups
  // must be listed in every language we serve.
  const tagGroups = React.useMemo(() => {
    if (!Array.isArray(tags)) return [];
    const HIDDEN_GROUPS = new Set([
      'Work subject',
      'Work aim',
      'Предмет праці',
      'Мета праці'
    ]);
    const groups = new Map();
    for (const tag of tags) {
      const label = localized(tag.name, lang);
      const groupKey = label || '__ungrouped';
      if (HIDDEN_GROUPS.has(groupKey.trim())) continue;
      const existing = groups.get(groupKey);
      const optionLabel = localized(tag.value, lang);
      const option = { _id: tag._id, name: optionLabel, value: optionLabel };
      if (existing) {
        existing.options.push(option);
        existing.ids.push(tag._id);
      } else {
        groups.set(groupKey, {
          key: groupKey,
          label,
          options: [option],
          ids: [tag._id]
        });
      }
    }
    return Array.from(groups.values());
  }, [tags, lang]);

  const handleGroupTagsChange = React.useCallback(
    (groupIds) => (newIds) => {
      const currentTagIds = filters?.tag_ids || [];
      const otherGroupIds = currentTagIds.filter((id) => !groupIds.includes(id));
      handleChangeTags([...otherGroupIds, ...newIds]);
    },
    [filters, handleChangeTags]
  );

  const [skillsInput, setSkillsInput] = React.useState('');

  return (
    <FiltersWrapper key={key}>
      <FiltersTitle>{t('Filters')}</FiltersTitle>

      <SelectChip
        options={categories}
        name={t('Categories')}
        multiple={true}
        selected={filters?.category_ids || []}
        onChange={handleChangeCategories}
      />

      {tagGroups.map((group) => (
        <SelectChip
          key={group.key}
          options={group.options}
          name={group.label}
          multiple={true}
          selected={(filters?.tag_ids || []).filter((id) => group.ids.includes(id))}
          onChange={handleGroupTagsChange(group.ids)}
        />
      ))}

      <Autocomplete
        multiple
        open={skillsInput.length > 0}
        inputValue={skillsInput}
        onInputChange={(_, newInput, reason) => {
          if (reason === 'reset') return;
          setSkillsInput(newInput);
        }}
        options={skills || []}
        value={(skills || []).filter((s) => (filters?.skill_ids || []).includes(s._id))}
        onChange={(_, newValue) => {
          handleChangeSkills(newValue.map((s) => s._id));
          setSkillsInput('');
        }}
        getOptionLabel={skillLabel}
        isOptionEqualToValue={(opt, val) => opt._id === val._id}
        filterSelectedOptions
        popupIcon={null}
        forcePopupIcon={false}
        clearOnBlur={false}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const tagProps = getTagProps({ index });
            return (
              <Chip
                key={option._id}
                label={skillLabel(option)}
                size="small"
                {...tagProps}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('Skills')}
            placeholder={t('Search')}
          />
        )}
        sx={{ mb: 2, width: 350 }}
      />

      <Button
        variant="outlined"
        color="primary"
        fullWidth={true}
        sx={{ backgroundColor: '#ffffff' }}
        onClick={() => {
          actions.clearFilters();
          setTimeout(() => setKey(uuid()), 200);
        }}>
        {t('ClearFilters')}
      </Button>
    </FiltersWrapper>
  );
};

export default Filters;
